import {
	CustomerSupplierControllerApiFactory,
	OrganisationInvitationControllerApiFactory,
	type OrganisationInvitationSentResponse,
	type OrganizationInvitationSentListResponse,
	type SuppliersResponse,
} from '@api-client/api'
import { QueryKey } from '@helpers/enums'
import { useAsyncApi } from '@hooks/useAsyncApi'
import { useQuery } from '@tanstack/react-query'
import type { AxiosResponse } from 'axios'
import { useCallback, useMemo } from 'react'

interface OrganisationInvitationSentResponseWithId extends OrganisationInvitationSentResponse {
	id: string
}

export interface Supplier {
	id: string
	name: string
}

const useSuppliersList = () => {
	const customerSupplierControllerApi = useAsyncApi(
		CustomerSupplierControllerApiFactory
	)
	const { data, isLoading, isError, refetch } = useQuery<
		unknown,
		unknown,
		AxiosResponse<SuppliersResponse>
	>({
		queryKey: [QueryKey.GetSuppliers],
		queryFn: () =>
			customerSupplierControllerApi(api => api.listSuppliers(0, 500)),
	})

	// Invited Suppliers.
	const organisationInvitationControllerApi = useAsyncApi(
		OrganisationInvitationControllerApiFactory
	)
	const {
		data: invitedSuppliersData,
		isLoading: areInvitedSuppliersLoading,
		isError: isInvitedSuppliersInError,
		refetch: refetchInvitedSuppliers,
	} = useQuery({
		queryKey: [QueryKey.GetInvitedSuppliers],
		queryFn: async () => {
			const response: AxiosResponse<OrganizationInvitationSentListResponse> =
				await organisationInvitationControllerApi(api =>
					api.listSentInvitations(0, 500, ['PENDING'])
				)
			if (!response.data) {
				return undefined
			}
			const { data: suppliersList, ...responseData } = response.data
			return {
				...responseData,
				data: suppliersList
					?.filter(
						(supplier): supplier is OrganisationInvitationSentResponseWithId =>
							!!supplier.id
					)
					?.map(({ id, recipientEmail }) => ({
						id,
						name: `${recipientEmail} (invited)`,
					})),
			}
		},
	})

	// Merge the two results.
	const refetchAll = useCallback(() => {
		refetch()
		refetchInvitedSuppliers()
	}, [refetch, refetchInvitedSuppliers])

	const allData = useMemo(() => {
		const suppliersData = data?.data
		return {
			metadata: {
				...suppliersData?.metadata,
				count:
					(suppliersData?.metadata?.count || 0) +
					(invitedSuppliersData?.metadata?.count || 0),
			},
			data: [
				...(suppliersData?.data || []),
				...(invitedSuppliersData?.data || []),
			],
		}
	}, [data, invitedSuppliersData])

	return {
		data: allData,
		loading: isLoading || areInvitedSuppliersLoading,
		error: isError || isInvitedSuppliersInError,
		refetch: refetchAll,
	}
}

export default useSuppliersList
